import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface DeleteTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: any;
}

export function DeleteTemplateDialog({ open, onOpenChange, template }: DeleteTemplateDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  const handleDelete = async () => {
    if (!template) return;

    try {
      setIsDeleting(true);

      // Eliminar la plantilla
      const { error } = await projectClient
        .from("report_templates_v2")
        .delete()
        .eq("id", template.id);

      if (error) throw error;

      toast.success("Plantilla eliminada correctamente");
      queryClient.invalidateQueries({ queryKey: ["templates-v2", currentProject] });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error al eliminar plantilla:", error);
      toast.error(error.message || "Error al eliminar la plantilla");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Eliminar Plantilla
          </DialogTitle>
          <DialogDescription>
            ¿Estás seguro de que deseas eliminar la plantilla "{template?.name || 'Sin nombre'}"?
            Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="text-sm text-destructive font-medium">
              ⚠️ Esta acción eliminará permanentemente:
            </div>
            <ul className="text-sm text-destructive mt-2 space-y-1">
              <li>• La plantilla y su configuración</li>
              <li>• Todas las variables asociadas</li>
              <li>• Los mapeos de variables</li>
              <li>• Las transformaciones configuradas</li>
              <li>• Los gráficos asociados</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Eliminando..." : "Eliminar Plantilla"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}